import React, { Fragment, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { SUBSCRIPTION_PLANS } from '../../../utils/stripe';

const NewKinnSellerPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('yearly'); // 'yearly' or 'monthly'
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSelectPlan = async (planType) => {
    if (!isAuthenticated) {
      // Redirect to signup with plan info in URL params
      window.location.href = `/auth/signup?plan=${planType}&billing=${billingPeriod}`;
      return;
    }

    // User is authenticated - create Stripe checkout session directly
    setLoading(true);

    try {
      const planConfig = SUBSCRIPTION_PLANS[planType][billingPeriod];

      // Create Stripe Checkout Session via Edge Function
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId: planConfig.priceId,
          planTier: planType,
          billingPeriod: billingPeriod,
          userId: user.id,
          userEmail: user.email,
          successUrl: `${window.location.origin}/subscription?success=true&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/kinn-seller#pricing`,
        },
      });

      if (error) {
        let serverMessage = '';
        if (error.context) {
          try {
            const cloned = error.context.clone();
            const body = await cloned.json();
            serverMessage = body?.error ?? JSON.stringify(body);
          } catch (parseError) {
            serverMessage = `Status ${error.context.status}`;
          }
        }
        console.error('Checkout error:', error, serverMessage);
        alert(serverMessage || 'Failed to start checkout. Please try again.');
        setLoading(false);
        return;
      }

      // Redirect to Stripe Checkout
      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to create checkout session. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-screen pt-[88px] lg:pt-[98px] -mt-[88px] lg:-mt-[98px] bg-gradient-to-br from-aurora-accent to-white flex flex-wrap pb-0">
        <Container className="relative z-[2] w-full pt-[80px]">
          {/* Hero Banner - Recreating seller-banner.png in HTML */}
          <div className="w-full max-w-[1300px] mx-auto px-4 mb-16">
            <div className="bg-white rounded-[30px] p-8 md:p-12 shadow-xl relative overflow-hidden">
              {/* Decorative background pattern */}
              <div className="absolute top-0 right-0 w-1/3 h-full opacity-5">
                <div className="grid grid-cols-4 gap-4 h-full">
                  {[...Array(16)].map((_, i) => (
                    <div key={i} className="bg-accent-blue rounded-full"></div>
                  ))}
                </div>
              </div>

              <div className="relative z-10">
                <Row className="items-center gap-y-8">
                  <Col lg={6}>
                    <div className="mb-6">
                      <span className="inline-block bg-accent-blue text-text-primary px-4 py-2 rounded-full text-body-md font-semibold mb-4">
                        📱 New: Kinn Seller App
                      </span>
                    </div>
                    <h1 className="text-h1 text-text-primary mb-6">
                      Manage Your Food Business.
                      <br />
                      <span className="text-accent-blue">Grow With Confidence.</span>
                    </h1>
                    <p className="text-body-xl-regular text-text-primary-secondary mb-8">
                      The all-in-one platform for small food sellers. Manage your shop, track orders, and reach more
                      customers—all from your phone.
                    </p>

                    <div className="flex flex-wrap items-center gap-4">
                      <NavLink to="/seller/get-started" className="btn-primary bg-surface-default border-shell-border hover:bg-gray-800">
                        Apply to Sell
                      </NavLink>
                      <NavLink to="/seller/onboarding" className="btn-secondary">
                        Complete your onboarding
                      </NavLink>
                    </div>

                    <div className="flex items-center gap-4 mt-6">
                      <div className="flex -space-x-3">
                        <img src="/images/user-1.png" className="w-10 h-10 rounded-full border-2 border-white" alt="Seller 1" />
                        <img src="/images/user-2.png" className="w-10 h-10 rounded-full border-2 border-white" alt="Seller 2" />
                        <img src="/images/user-3.png" className="w-10 h-10 rounded-full border-2 border-white" alt="Seller 3" />
                      </div>
                      <div>
                        <p className="text-body-lg-regular text-text-primary">Join hundreds of verified sellers</p>
                        <p className="text-body-sm-regular text-text-primary-secondary">Safe, trusted, and supported by Kinn</p>
                      </div>
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="relative">
                      <div className="absolute -top-12 -left-6 w-24 h-24 bg-accent-blue rounded-full blur-3xl opacity-50"></div>
                      <div className="absolute -bottom-10 -right-4 w-32 h-32 bg-gradient-to-br from-[#C2E9FB] to-[#A1C4FD] rounded-full blur-3xl opacity-50"></div>
                      <div className="relative bg-gradient-to-br from-[#C2E9FB] via-white to-aurora-accent/20 rounded-[30px] p-6 shadow-lg border border-white/60">
                        <div className="bg-white rounded-[20px] p-4 mb-4 shadow-sm">
                          <div className="flex justify-between items-center mb-4">
                            <div>
                              <h4 className="text-body-lg font-semibold text-text-primary mb-1">Today&apos;s Orders</h4>
                              <p className="text-body-sm text-text-primary-secondary">Kinn Seller App</p>
                            </div>
                            <span className="bg-accent-blue px-4 py-2 rounded-full text-body-sm font-semibold text-text-primary">
                              Live
                            </span>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-body-md font-semibold text-text-primary">Coconut Jollof</p>
                                <p className="text-body-sm text-text-primary-secondary">Ama’s Kitchen</p>
                              </div>
                              <span className="text-body-md font-semibold text-green-600">£45</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-body-md font-semibold text-text-primary">Vegan Mezze Platter</p>
                                <p className="text-body-sm text-text-primary-secondary">Lina&apos;s Mezze</p>
                              </div>
                              <span className="text-body-md font-semibold text-green-600">£38</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-body-md font-semibold text-text-primary">Accra Kelewele</p>
                                <p className="text-body-sm text-text-primary-secondary">Accra Kitchen</p>
                              </div>
                              <span className="text-body-md font-semibold text-green-600">£26</span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white rounded-[20px] p-4 shadow-sm">
                            <p className="text-body-sm text-text-primary-secondary">Monthly Revenue</p>
                            <h4 className="text-h4-normal text-text-primary mt-2">£3,640</h4>
                            <p className="text-body-sm text-green-600 mt-1">+18% vs last month</p>
                          </div>
                          <div className="bg-white rounded-[20px] p-4 shadow-sm">
                            <p className="text-body-sm text-text-primary-secondary">Repeat Customers</p>
                            <h4 className="text-h4-normal text-text-primary mt-2">62%</h4>
                            <p className="text-body-sm text-green-600 mt-1">+12% improvement</p>
                          </div>
                        </div>

                        <div className="bg-white rounded-[20px] p-4 mt-4 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C2E9FB] to-[#A1C4FD] flex items-center justify-center text-body-lg font-semibold text-text-primary">
                              AI
                            </span>
                            <h4 className="text-body-lg font-semibold text-text-primary">Smart Menu Assistant</h4>
                          </div>
                          <p className="text-body-sm text-text-primary-secondary">
                            “Add &lt;strong&gt;Smoked Jollof Arancini&lt;/strong&gt; as a weekend special — trending in your area and aligns with your
                            top customer preferences.”
                          </p>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="relative overflow-hidden py-[80px]">
        {/* Aurora gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-aurora-gradient-start/20 via-white to-aurora-gradient-end/10"></div>
        
        <Container className="relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-h2-normal text-text-primary mb-4">Everything You Need to Succeed</h2>
            <p className="text-body-lg-regular text-text-primary-secondary max-w-2xl mx-auto">
              Professional tools designed for small food sellers, home bakers, caterers, and food entrepreneurs.
            </p>
          </div>

          <Row className="gap-y-8">
            <Col md={6} lg={4}>
              <div className="relative bg-white rounded-[20px] p-6 h-full border-2 border-shell-border hover:border-accent-blue transition-all overflow-hidden group">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-aurora-gradient-start/30 to-transparent rounded-bl-full"></div>
                <div className="w-16 h-16 bg-gradient-to-r from-aurora-primary to-accent-blue-dark rounded-full flex items-center justify-center mb-4 relative z-10">
                  <span className="text-3xl">📱</span>
                </div>
                <h3 className="text-body-xl font-semibold text-text-primary mb-3">Menu & Stock Management</h3>
                <p className="text-body-md-regular text-text-primary-secondary">
                  Update offerings and track inventory in real-time
                </p>
              </div>
            </Col>

            <Col md={6} lg={4}>
              <div className="relative bg-white rounded-[20px] p-6 h-full border-2 border-shell-border hover:border-accent-blue transition-all overflow-hidden group">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-aurora-gradient-start/30 to-transparent rounded-bl-full"></div>
                <div className="w-16 h-16 bg-gradient-to-r from-aurora-primary to-accent-blue-dark rounded-full flex items-center justify-center mb-4 relative z-10">
                  <span className="text-3xl">�</span>
                </div>
                <h3 className="text-body-xl font-semibold text-text-primary mb-3">Integrated Payments</h3>
                <p className="text-body-md-regular text-text-primary-secondary">
                  Secure, straightforward payments processed directly to you
                </p>
              </div>
            </Col>

            <Col md={6} lg={4}>
              <div className="relative bg-white rounded-[20px] p-6 h-full border-2 border-shell-border hover:border-accent-blue transition-all overflow-hidden group">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-aurora-gradient-start/30 to-transparent rounded-bl-full"></div>
                <div className="w-16 h-16 bg-gradient-to-r from-aurora-primary to-accent-blue-dark rounded-full flex items-center justify-center mb-4 relative z-10">
                  <span className="text-3xl">📊</span>
                </div>
                <h3 className="text-body-xl font-semibold text-text-primary mb-3">Unified Order Dashboard</h3>
                <p className="text-body-md-regular text-text-primary-secondary">
                  See and manage all orders from one screen
                </p>
              </div>
            </Col>

            <Col md={6} lg={4}>
              <div className="relative bg-white rounded-[20px] p-6 h-full border-2 border-shell-border hover:border-accent-blue transition-all overflow-hidden group">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-aurora-gradient-start/30 to-transparent rounded-bl-full"></div>
                <div className="w-16 h-16 bg-gradient-to-r from-aurora-primary to-accent-blue-dark rounded-full flex items-center justify-center mb-4 relative z-10">
                  <span className="text-3xl">🤖</span>
                </div>
                <h3 className="text-body-xl font-semibold text-text-primary mb-3">AI Allergen Detective</h3>
                <p className="text-body-md-regular text-text-primary-secondary">
                  Upload recipe, AI flags UK allergens instantly
                </p>
              </div>
            </Col>

            <Col md={6} lg={4}>
              <div className="relative bg-white rounded-[20px] p-6 h-full border-2 border-shell-border hover:border-accent-blue transition-all overflow-hidden group">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-aurora-gradient-start/30 to-transparent rounded-bl-full"></div>
                <div className="w-16 h-16 bg-gradient-to-r from-aurora-primary to-accent-blue-dark rounded-full flex items-center justify-center mb-4 relative z-10">
                  <span className="text-3xl">📝</span>
                </div>
                <h3 className="text-body-xl font-semibold text-text-primary mb-3">AI Ingredient Generator</h3>
                <p className="text-body-md-regular text-text-primary-secondary">
                  Create professional ingredient lists in seconds
                </p>
              </div>
            </Col>

            <Col md={6} lg={4}>
              <div className="relative bg-white rounded-[20px] p-6 h-full border-2 border-shell-border hover:border-accent-blue transition-all overflow-hidden group">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-aurora-gradient-start/30 to-transparent rounded-bl-full"></div>
                <div className="w-16 h-16 bg-gradient-to-r from-aurora-primary to-accent-blue-dark rounded-full flex items-center justify-center mb-4 relative z-10">
                  <span className="text-3xl">🏪</span>
                </div>
                <h3 className="text-body-xl font-semibold text-text-primary mb-3">Exclusive Marketplace Access</h3>
                <div className="flex items-center gap-2 mb-2">
                <span className="inline-block bg-accent-blue text-text-primary px-2 py-1 rounded-full text-body-sm font-semibold">
                  Coming Soon
                </span>
                </div>
                <p className="text-body-md-regular text-text-primary-secondary">
                  Get discovered by food lovers
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Pricing Section */}
      <section className="relative overflow-hidden py-[100px]" id="pricing">
        {/* Aurora gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-aurora-gradient-start/20 via-white to-aurora-gradient-end/10"></div>
        
        <Container className="relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-h2-normal text-text-primary mb-4">Simple, Transparent Pricing</h2>
            <p className="text-body-lg-regular text-text-primary-secondary max-w-2xl mx-auto mb-8">
              One plan. All the tools you need to grow your food business.
            </p>

            {/* Billing Period Toggle */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <span className={`text-body-md ${billingPeriod === 'monthly' ? 'font-semibold text-text-primary' : 'text-text-primary-secondary'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'yearly' ? 'monthly' : 'yearly')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  billingPeriod === 'yearly' ? 'bg-gradient-to-r from-aurora-primary to-accent-blue-dark' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                    billingPeriod === 'yearly' ? 'right-1' : 'left-1'
                  }`}
                ></span>
              </button>
              <span className={`text-body-md ${billingPeriod === 'yearly' ? 'font-semibold text-text-primary' : 'text-text-primary-secondary'}`}>
                Yearly <span className="text-green-600 text-sm font-semibold">(Save 34%)</span>
              </span>
            </div>
          </div>

          <Row className="justify-center">
            {/* Kinn Starter Plan - Single Plan */}
            <Col lg={8} xl={6}>
              <div className="relative bg-white rounded-[30px] p-10 shadow-2xl border-2 border-shell-border hover:border-accent-blue transition-all overflow-hidden">
                {/* Aurora gradient overlay */}
                <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-aurora-gradient-start/20 to-transparent pointer-events-none"></div>
                
                <div className="relative z-10">
                  <div className="mb-6">
                    <span className="inline-block bg-gradient-to-r from-aurora-primary to-accent-blue-dark text-white px-4 py-2 rounded-full text-body-sm font-semibold mb-4">
                      ✨ Everything You Need
                    </span>
                    <h3 className="text-h2 text-text-primary mb-2">Kinn Starter</h3>
                    <p className="text-body-lg-regular text-text-primary-secondary">Perfect for growing your food business</p>
                  </div>

                  <div className="mb-6">
                    {billingPeriod === 'yearly' ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-5xl font-bold bg-gradient-to-r from-aurora-primary to-accent-blue-dark bg-clip-text text-transparent">£8.25</span>
                          <span className="text-body-lg-regular text-text-primary-secondary">/month</span>
                        </div>
                        <p className="text-body-sm-regular text-text-primary-secondary mt-2">£99 billed yearly - Save 34%</p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-5xl font-bold bg-gradient-to-r from-aurora-primary to-accent-blue-dark bg-clip-text text-transparent">£15</span>
                          <span className="text-body-lg-regular text-text-primary-secondary">/month</span>
                        </div>
                        <p className="text-body-sm-regular text-text-primary-secondary mt-2">Billed monthly</p>
                      </>
                    )}
                  </div>

                  <div className="space-y-3 mb-8">
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-aurora-primary to-accent-blue-dark flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">
                        ✓
                      </span>
                      <div>
                        <p className="text-body-md font-semibold text-text-primary">Menu & Stock Management</p>
                        <p className="text-body-sm text-text-primary-secondary">Update offerings and track inventory in real-time</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-aurora-primary to-accent-blue-dark flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">
                        ✓
                      </span>
                      <div>
                        <p className="text-body-md font-semibold text-text-primary">Integrated Payments</p>
                        <p className="text-body-sm text-text-primary-secondary">Secure, straightforward payments processed directly to you</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-aurora-primary to-accent-blue-dark flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">
                        ✓
                      </span>
                      <div>
                        <p className="text-body-md font-semibold text-text-primary">Unified Order Dashboard</p>
                        <p className="text-body-sm text-text-primary-secondary">See and manage all orders from one screen</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-aurora-primary to-accent-blue-dark flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">
                        ✓
                      </span>
                      <div>
                        <p className="text-body-md font-semibold text-text-primary">AI Allergen Detective</p>
                        <p className="text-body-sm text-text-primary-secondary">Upload recipe, AI flags UK allergens instantly</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-aurora-primary to-accent-blue-dark flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">
                        ✓
                      </span>
                      <div>
                        <p className="text-body-md font-semibold text-text-primary">AI Ingredient Generator</p>
                        <p className="text-body-sm text-text-primary-secondary">Create professional ingredient lists in seconds</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-aurora-primary to-accent-blue-dark flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">
                        ✓
                      </span>
                      <div className="flex items-center gap-2">
                        <p className="text-body-md font-semibold text-text-primary">Exclusive Marketplace Access</p>
                        <span className="inline-block bg-accent-blue text-text-primary px-2 py-1 rounded-full text-xs font-semibold">
                          Coming Soon
                        </span>
                      </div>
                      <p className="text-body-sm text-text-primary-secondary">Get discovered by food lovers</p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleSelectPlan('starter')}
                    disabled={loading}
                    className="w-full text-center py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-aurora-primary to-accent-blue-dark hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    {loading ? 'Loading...' : 'Launch Your Kinn Store'}
                  </button>
                  
                  <p className="text-center text-body-sm text-text-primary-secondary mt-4">
                    💡 30-day free trial • Cancel anytime • No hidden fees
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Additional Info */}
          <div className="text-center mt-12">
            <p className="text-body-md-regular text-text-primary-secondary">💡 30-day free trial • Cancel anytime • No hidden fees</p>
          </div>
        </Container>
      </section>

      {/* Why Kinn Section */}
      <section className="bg-gradient-to-br from-[#C2E9FB] to-[#A1C4FD] py-[80px]">
        <Container>
          <Row className="items-center gap-y-8">
            <Col lg={6}>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-h2-normal">💚</span>
                <h2 className="text-h2-normal text-text-primary mb-0">Why Sellers Choose Kinn</h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-body-xl text-green-600 mt-1">✓</span>
                  <div>
                    <h3 className="text-body-xl font-semibold text-text-primary mb-1">Built for Food Sellers</h3>
                    <p className="text-body-md-regular text-text-primary-secondary">
                      Not a generic e-commerce tool. Every feature designed specifically for food businesses.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-body-xl text-green-600 mt-1">✓</span>
                  <div>
                    <h3 className="text-body-xl font-semibold text-text-primary mb-1">Verified &amp; Trusted</h3>
                    <p className="text-body-md-regular text-text-primary-secondary">
                      Manual verification builds buyer trust and keeps standards high.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-body-xl text-green-600 mt-1">✓</span>
                  <div>
                    <h3 className="text-body-xl font-semibold text-text-primary mb-1">Fair Pricing</h3>
                    <p className="text-body-md-regular text-text-primary-secondary">
                      No hidden fees or commission on sales. Just simple annual pricing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-body-xl text-green-600 mt-1">✓</span>
                  <div>
                    <h3 className="text-body-xl font-semibold text-text-primary mb-1">Community Support</h3>
                    <p className="text-body-md-regular text-text-primary-secondary">
                      Join a network of passionate food entrepreneurs helping each other grow.
                    </p>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="rounded-[20px] overflow-hidden">
                <img src="/images/a-woman-cooking-fun.jpg" className="w-full h-auto" alt="Happy food seller" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-aurora-gradient-start to-aurora-gradient-end py-[100px]">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-h1 text-text-primary mb-6">Ready to Grow Your Food Business?</h2>
            <p className="text-body-xl-regular text-gray-800 mb-8">
              Join hundreds of food sellers already managing their shops with Kinn Seller.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <NavLink 
                to="/auth/signup" 
                className="btn-primary px-8 py-4 text-body-lg bg-gradient-to-r from-aurora-primary to-accent-blue-dark text-white border-0 hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Launch Your Kinn Store
              </NavLink>
              <NavLink to="/founding-sellers" className="btn-secondary px-8 py-4 text-body-lg border-accent-blue text-accent-blue hover:bg-aurora-bg-secondary">
                Become a Founding Seller
              </NavLink>
            </div>
            <p className="text-body-sm text-text-primary-secondary">
              💡 30-day free trial • Cancel anytime
            </p>
            <p className="text-body-sm text-text-primary mt-2">
              Questions? Email us at{' '}
              <a href="mailto:hello@kinn.menu" className="underline font-semibold text-accent-blue hover:text-accent-blue-hover">
                hello@kinn.menu
              </a>
            </p>
          </div>
        </Container>
      </section>
    </Fragment>
  );
};

export default NewKinnSellerPage;
